import { SLOTS, SLOT_POS, FORMATIONS, FORMATION_NAMES, LEAGUES } from "./constants";
import { makeRng, hashSeed } from "./rng";
import { NAMES, CLUBS, RIVAL_HANDLES } from "./names";

const OUTFIELD = SLOTS.filter((s) => s !== "GK");

function slotDist(a, b) {
  const pa = SLOT_POS[a], pb = SLOT_POS[b];
  return Math.hypot(pa.x - pb.x, pa.y - pb.y) / 130; // normalized ~0..1
}

// primary position pools by role
const ROLE_POOLS = {
  GK: ["GK"],
  DEF: ["LB", "LCB", "CB", "RCB", "RB", "LWB", "RWB"],
  MID: ["DM", "LCM", "CM", "RCM", "AM", "LWB", "RWB"],
  ATT: ["LW", "RW", "ST", "LST", "RST", "AM"],
};

function eligibleFor(rng, primary, count) {
  // pick nearby slots by distance
  const others = OUTFIELD.filter((s) => s !== primary)
    .map((s) => ({ s, d: slotDist(primary, s) }))
    .sort((a, b) => a.d - b.d);
  const near = others.slice(0, 6);
  const chosen = rng.shuffle(near).slice(0, count - 1).map((x) => x.s);
  return [primary, ...chosen];
}

function buildClubs() {
  const clubs = [];
  LEAGUES.forEach((lg) => {
    CLUBS[lg.id].forEach((c) => {
      const rng = makeRng(hashSeed("club" + lg.id + c.code));
      const np = NAMES[lg.langHint];
      const coachName = `${rng.pick(np.first)} ${rng.pick(np.last)}`;
      clubs.push({ ...c, id: `${lg.id}-${c.code}`, leagueId: lg.id, langHint: lg.langHint, coachName });
    });
  });
  return clubs;
}

// Build a 25-man roster for a club (identities only)
function buildRoster(club) {
  const rng = makeRng(hashSeed("roster" + club.id));
  const np = NAMES[club.langHint];
  const players = [];
  const usedNums = new Set();
  const roleCounts = [
    ["GK", 3], ["DEF", 8], ["MID", 8], ["ATT", 6],
  ];
  let idx = 0;
  roleCounts.forEach(([role, n]) => {
    for (let i = 0; i < n; i++) {
      let num;
      do { num = rng.int(1, 45); } while (usedNums.has(num));
      usedNums.add(num);
      const primary = role === "GK" ? "GK" : rng.pick(ROLE_POOLS[role]);
      const eligible = role === "GK" ? ["GK"] : eligibleFor(rng, primary, rng.int(2, 4));
      const first = rng.pick(np.first);
      const last = rng.pick(np.last);
      // archetype: 'utility' | 'trap' | 'normal'
      let archetype = "normal";
      if (role !== "GK") {
        if (i === 0 && (role === "MID" || role === "DEF")) archetype = "utility";
        if (i === 1 && role === "ATT") archetype = "trap";
      }
      players.push({
        id: `${club.id}-${num}`,
        clubId: club.id,
        number: num,
        firstName: first,
        surname: last,
        name: `${first} ${last}`,
        age: rng.int(18, 34),
        primary,
        eligible,
        role,
        archetype,
        seed: idx++,
      });
    }
  });
  return players;
}

// value matrix for a player inside a specific fixture
function playerValues(player, fxSeed) {
  const rng = makeRng(hashSeed(player.id + fxSeed));
  const values = {};
  if (player.primary === "GK") {
    values.GK = rng.round4(rng.range(0.04, 0.12));
    return values;
  }
  const base = player.archetype === "trap"
    ? rng.range(0.34, 0.45)
    : rng.range(0.16, 0.4);
  const degrade = player.archetype === "utility"
    ? rng.range(0.12, 0.28)
    : player.archetype === "trap"
      ? rng.range(1.35, 1.7)
      : rng.range(0.85, 1.15);
  player.eligible.forEach((slot) => {
    const d = slot === player.primary ? 0 : slotDist(player.primary, slot);
    let v = base * (1 - degrade * d);
    v += rng.range(-0.02, 0.02);
    v = Math.max(-0.2, Math.min(0.45, v));
    values[slot] = rng.round4(v);
  });
  return values;
}

// Build an 18-man matchday squad from roster with fixture-specific values
function buildSquad(roster, fxSeed) {
  const rng = makeRng(hashSeed("squad" + fxSeed));
  // ensure positional coverage: 2 GK + 16 outfield, keep archetypes
  const gks = roster.filter((p) => p.role === "GK");
  const outfield = roster.filter((p) => p.role !== "GK");
  const chosenGk = rng.shuffle(gks).slice(0, 2);
  // keep utility + trap in
  const special = outfield.filter((p) => p.archetype !== "normal");
  const normals = rng.shuffle(outfield.filter((p) => p.archetype === "normal"));
  const chosenOut = [...special, ...normals].slice(0, 16);
  const squad = [...chosenGk, ...chosenOut].map((p) => {
    const values = playerValues(p, fxSeed);
    return { ...p, values };
  });
  // availability flags: 2-4 doubtful, occasionally one late-unavailable
  const doubtCount = rng.int(2, 4);
  const shuffled = rng.shuffle(squad.filter((p) => p.role !== "GK"));
  shuffled.slice(0, doubtCount).forEach((p) => {
    p.availabilityProbability = rng.round4(rng.range(0.55, 0.9));
  });
  if (rng.chance(0.4)) {
    const late = shuffled[doubtCount];
    if (late) { late.availabilityProbability = 0.15; late.lateUnavailable = true; }
  }
  squad.forEach((p) => { if (p.availabilityProbability === undefined) p.availabilityProbability = 1; });
  return squad;
}

function egdOf(assignment, squadById) {
  let sum = 0;
  for (const slot in assignment) {
    const p = squadById[assignment[slot]];
    const v = p?.values?.[slot];
    sum += v === undefined ? -0.1 : v;
  }
  return sum;
}

// best assignment for one formation via greedy + local search
function bestAssignment(formationSlots, squad) {
  const byId = {};
  squad.forEach((p) => (byId[p.id] = p));
  const triples = [];
  formationSlots.forEach((slot) => {
    squad.forEach((p) => {
      if (p.values[slot] !== undefined) triples.push([slot, p.id, p.values[slot]]);
    });
  });
  triples.sort((a, b) => b[2] - a[2]);
  const assign = {};
  const usedP = new Set();
  const usedS = new Set();
  triples.forEach(([slot, pid]) => {
    if (!usedS.has(slot) && !usedP.has(pid)) {
      assign[slot] = pid; usedS.add(slot); usedP.add(pid);
    }
  });
  // fill any unfilled slot with any free player
  formationSlots.forEach((slot) => {
    if (!assign[slot]) {
      const free = squad.find((p) => !usedP.has(p.id));
      if (free) { assign[slot] = free.id; usedP.add(free.id); }
    }
  });
  // local search
  let improved = true, guard = 0;
  while (improved && guard++ < 60) {
    improved = false;
    // pairwise swap
    for (let i = 0; i < formationSlots.length; i++) {
      for (let j = i + 1; j < formationSlots.length; j++) {
        const s1 = formationSlots[i], s2 = formationSlots[j];
        const p1 = byId[assign[s1]], p2 = byId[assign[s2]];
        const cur = (p1.values[s1] ?? -0.1) + (p2.values[s2] ?? -0.1);
        const v1 = p1.values[s2], v2 = p2.values[s1];
        if (v1 !== undefined && v2 !== undefined && v1 + v2 > cur + 1e-9) {
          assign[s1] = p2.id; assign[s2] = p1.id; improved = true;
        }
      }
    }
    // benched replace
    const inUse = new Set(Object.values(assign));
    const benched = squad.filter((p) => !inUse.has(p.id));
    formationSlots.forEach((slot) => {
      const cur = byId[assign[slot]];
      const curV = cur.values[slot] ?? -0.1;
      let bestB = null, bestV = curV;
      benched.forEach((b) => {
        if (b.values[slot] !== undefined && b.values[slot] > bestV + 1e-9) { bestB = b; bestV = b.values[slot]; }
      });
      if (bestB) {
        assign[slot] = bestB.id;
        improved = true;
        const bi = benched.indexOf(bestB); if (bi >= 0) benched.splice(bi, 1);
        benched.push(cur);
      }
    });
  }
  return { assignment: assign, egd: egdOf(assign, byId) };
}

function computeOptimal(squad) {
  let best = null;
  FORMATION_NAMES.forEach((f) => {
    const r = bestAssignment(FORMATIONS[f], squad);
    if (!best || r.egd > best.egd) best = { ...r, formation: f };
  });
  return best;
}

// degrade optimal into a plausible coach XI with target LGD
function degradeXI(squad, optimal, seedStr, targetLo, targetHi) {
  const byId = {};
  squad.forEach((p) => (byId[p.id] = p));
  const rng = makeRng(hashSeed(seedStr));
  const slots = FORMATIONS[optimal.formation];
  for (let attempt = 0; attempt < 240; attempt++) {
    const assign = { ...optimal.assignment };
    const k = rng.int(2, 4);
    const inUse = new Set(Object.values(assign));
    let benched = squad.filter((p) => !inUse.has(p.id));
    const targets = rng.shuffle(slots.filter((s) => s !== "GK")).slice(0, k);
    targets.forEach((slot) => {
      const cands = benched.filter((b) => b.values[slot] !== undefined);
      if (cands.length && rng.chance(0.7)) {
        const b = rng.pick(cands);
        const out = byId[assign[slot]];
        assign[slot] = b.id;
        benched = benched.filter((x) => x.id !== b.id);
        benched.push(out);
      } else {
        // swap two occupied slots to worse arrangement
        const other = rng.pick(slots.filter((s) => s !== slot && s !== "GK"));
        const pa = byId[assign[slot]], pb = byId[assign[other]];
        if (pa.values[other] !== undefined && pb.values[slot] !== undefined) {
          assign[slot] = pb.id; assign[other] = pa.id;
        }
      }
    });
    const egd = egdOf(assign, byId);
    const lgd = optimal.egd - egd;
    if (lgd >= targetLo && lgd <= targetHi) {
      return { assignment: assign, formation: optimal.formation, egd, lgd: Math.round(lgd * 10000) / 10000 };
    }
  }
  // fallback: clamp
  const egd = optimal.egd - (targetLo + targetHi) / 2;
  return { assignment: { ...optimal.assignment }, formation: optimal.formation, egd, lgd: Math.round((targetLo + targetHi) / 2 * 10000) / 10000 };
}

function round2(v) { return Math.round(v * 100) / 100; }

// ------------------ main build ------------------
export function buildDataset() {
  const clubs = buildClubs();
  const clubsById = {};
  clubs.forEach((c) => (clubsById[c.id] = c));
  const rosters = {};
  clubs.forEach((c) => (rosters[c.id] = buildRoster(c)));

  const TOTAL_MD = 15; // 12 completed + 1 live + 2 upcoming
  const LIVE_MD = 12; // index (0-based) -> matchday 13
  const nowBase = Date.now();

  const leagues = LEAGUES.map((lg) => {
    const lgClubs = clubs.filter((c) => c.leagueId === lg.id);
    const rng = makeRng(hashSeed("sched" + lg.id));
    const matchdays = [];
    for (let md = 0; md < TOTAL_MD; md++) {
      // round-robin-ish pairing (shuffle per matchday)
      const order = rng.shuffle(lgClubs);
      const fixtures = [];
      for (let i = 0; i < order.length; i += 2) {
        const home = order[i], away = order[i + 1];
        const fxId = `${lg.id}-md${md + 1}-${home.code}`;
        const isLive = md === LIVE_MD;
        const isCompleted = md < LIVE_MD;
        // kickoff staggered across the day
        const koHour = 12 + (i / 2); // 12:00,13:00...
        const kickoff = new Date(nowBase);
        kickoff.setHours(koHour, 30, 0, 0);
        const fxSeedHome = fxId + "H";
        const fxSeedAway = fxId + "A";

        let fixture = {
          id: fxId, leagueId: lg.id, matchday: md + 1,
          homeId: home.id, awayId: away.id,
          kickoff: kickoff.toISOString(),
          isLive, isCompleted, isUpcoming: md > LIVE_MD,
        };

        if (isLive) {
          const homeSquad = buildSquad(rosters[home.id], fxSeedHome);
          const optimal = computeOptimal(homeSquad);
          // designate primary demo fixture: first fixture of elite league live md
          const isPrimary = lg.id === "elite" && i === 0;
          const coachLo = isPrimary ? 0.26 : 0.08;
          const coachHi = isPrimary ? 0.34 : 0.42;
          const coachXI = degradeXI(homeSquad, optimal, fxId + "coach", coachLo, coachHi);
          // demo user XI (used if user hasn't built): beats coach on primary
          const userLo = isPrimary ? 0.06 : 0.1;
          const userHi = isPrimary ? 0.12 : 0.3;
          const demoUserXI = degradeXI(homeSquad, optimal, fxId + "user", userLo, userHi);
          // AI = a strong but imperfect predictor (sealed before official lineups),
          // small LGD so it is a visible, occasionally-beatable benchmark — not the raw optimal.
          const aiXI = degradeXI(homeSquad, optimal, fxId + "ai", 0.02, 0.08);
          const finalHome = rng.int(0, 4), finalAway = rng.int(0, 3);
          fixture.homeSquad = homeSquad;
          fixture.optimalEGD = Math.round(optimal.egd * 10000) / 10000;
          fixture.optimalXI = { assignment: optimal.assignment, formation: optimal.formation, egd: fixture.optimalEGD, lgd: 0 };
          fixture.aiXI = aiXI;
          fixture.coachXI = coachXI;
          fixture.demoUserXI = demoUserXI;
          fixture.finalScore = { home: finalHome, away: finalAway };
          fixture.actualGD = finalHome - finalAway;
          fixture.commitHash = hashHex(fxId + "commit");
          fixture.lockOffsetMs = (isPrimary ? 30 : 30 + i * 25 - 40) * 60000; // primary locks in 30 virtual min
          if (fixture.lockOffsetMs < -120 * 60000) fixture.lockOffsetMs = -20 * 60000;
          // default demo state staggering
          fixture.defaultState = i === 0 ? "OPEN" : i === 2 ? "LOCKED" : i === 4 ? "SETTLED" : "OPEN";
        } else if (isCompleted) {
          const fh = rng.int(0, 4), fa = rng.int(0, 4);
          fixture.finalScore = { home: fh, away: fa };
          fixture.actualGD = fh - fa;
          fixture.coachLGD = round4(rng.range(0.05, 0.45));
          fixture.aiLGD = rng.chance(0.15) ? round4(rng.range(0.02, 0.08)) : 0;
          fixture.defaultState = "SETTLED";
        } else {
          fixture.defaultState = "OPEN";
        }
        fixtures.push(fixture);
      }
      matchdays.push({ matchday: md + 1, isLive: md === LIVE_MD, fixtures });
    }
    return { ...lg, clubIds: lgClubs.map((c) => c.id), matchdays };
  });

  // ---- users: 60 rivals + 3 profiles ----
  const allClubIds = clubs.map((c) => c.id);
  const rivals = [];
  const rr = makeRng(hashSeed("rivals"));
  for (let i = 0; i < 60; i++) {
    const handle = `${rr.pick(RIVAL_HANDLES)}${rr.int(1, 99)}`;
    const fav = rr.pick(allClubIds);
    const skill = rr.range(0.1, 0.32); // avg lgd per md
    const history = makeHistory(rr, 12, skill);
    rivals.push({
      id: `rival-${i}`, displayName: handle, favClubId: fav,
      history, isRival: true,
    });
  }

  const profiles = {
    new: { id: "new", displayName: "You", favClubId: "elite-ASH", language: "en", history: [], profileType: "new" },
    mid: { id: "mid", displayName: "You", favClubId: "elite-ASH", language: "en", history: makeHistory(makeRng(hashSeed("midhist")), 12, 0.21), profileType: "mid" },
    elite: { id: "elite", displayName: "You", favClubId: "elite-ASH", language: "en", history: makeHistory(makeRng(hashSeed("elitehist")), 12, 0.11, true), profileType: "elite" },
  };

  return { clubs, clubsById, rosters, leagues, rivals, profiles, liveMatchday: LIVE_MD + 1, nowBase };
}

function makeHistory(rng, n, skill, elite = false) {
  const h = [];
  for (let md = 1; md <= n; md++) {
    let lgd = Math.max(0.02, Math.min(0.65, skill + rng.range(-0.08, 0.12)));
    const coachLGD = round4(rng.range(0.05, 0.45));
    // AI is a strong but imperfect predictor — small visible LGD, not a flat zero
    let aiLGD = round4(rng.range(0.02, 0.09));
    if (elite && (md === 4 || md === 9)) { aiLGD = round4(rng.range(0.06, 0.11)); lgd = Math.max(0.01, aiLGD - 0.03); }
    const actualGD = rng.pick([-2, -1, -1, 0, 0, 1, 1, 2, 3]);
    h.push({ matchday: md, lgd: round4(lgd), coachLGD, aiLGD, actualGD });
  }
  return h;
}

function round4(v) { return Math.round(v * 10000) / 10000; }

function hashHex(str) {
  let h = 0x811c9dc5;
  let out = "";
  for (let k = 0; k < 4; k++) {
    for (let i = 0; i < str.length; i++) { h ^= str.charCodeAt(i) + k; h = Math.imul(h, 0x01000193); }
    out += (h >>> 0).toString(16).padStart(8, "0");
  }
  return out.slice(0, 40);
}
